import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Star, Repeat } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getMenteeBookings, SessionBookingWithMentor } from '@/utils/menteeBookingsUtils';
import { format } from 'date-fns';
import { convertFromUTC, getUserTimezone } from '@/utils/timezoneUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { ReviewDialog } from '@/components/ReviewDialog';
import MenteeAppointmentDetailsDialog from './MenteeAppointmentDetailsDialog';

interface EndedSubscription {
  id: string;
  application_id: string;
  status: string;
  created_at: string;
  message: string;
  subscription_end_date: string;
  course: {
    id: string;
    title: string;
    price: number;
    course_type: string;
  };
  mentor: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string;
  };
  hasReview?: boolean;
  isSubscription: boolean;
}

type CompletedItem = SessionBookingWithMentor | EndedSubscription;

interface CompletedAppointmentsProps {
  onPendingReviewsChange?: (hasPendingReviews: boolean) => void;
}

const CompletedAppointments = ({ onPendingReviewsChange }: CompletedAppointmentsProps) => {
  const { user } = useAuth();
  const userTimezone = getUserTimezone();
  const [bookings, setBookings] = useState<SessionBookingWithMentor[]>([]);
  const [endedSubscriptions, setEndedSubscriptions] = useState<EndedSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<CompletedItem | null>(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadCompletedItemsWithoutReviews();
    }
  }, [user]);

  useEffect(() => {
    // Listen for subscription-ended events to refresh the pending reviews
    const handleSubscriptionEnded = () => {
      console.log('DEBUG: Subscription ended event received in CompletedAppointments, refreshing reviews');
      loadCompletedItemsWithoutReviews();
    };

    window.addEventListener('subscription-ended', handleSubscriptionEnded);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('subscription-ended', handleSubscriptionEnded);
    };
  }, []);

  const loadCompletedItemsWithoutReviews = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      console.log('DEBUG: Loading completed items for user:', user.id);
      
      // First, update any subscriptions that should be completed
      await updateEndedSubscriptions();
      
      // Load completed session bookings
      const sessionResult = await getMenteeBookings(user.id);
      console.log('DEBUG: getMenteeBookings result:', sessionResult);
      if (sessionResult.success && sessionResult.data) {
        console.log('DEBUG: All mentee bookings:', sessionResult.data);
        const completedBookings = sessionResult.data.filter(booking => 
          booking.status === 'completed'
        ).sort((a, b) => new Date(b.booked_date).getTime() - new Date(a.booked_date).getTime());
        console.log('DEBUG: Completed bookings after filter:', completedBookings);
        
        const bookingsWithoutReviews = await filterBookingsWithoutReviews(completedBookings);
        console.log('DEBUG: Bookings without reviews:', bookingsWithoutReviews);
        setBookings(bookingsWithoutReviews);
      }

      // Load ended subscriptions
      await loadEndedSubscriptions();
    } catch (error) {
      console.error('Error loading completed items:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEndedSubscriptions = async () => {
    try {
      console.log('DEBUG: Calling update_ended_subscriptions function');
      const { error } = await supabase.rpc('update_ended_subscriptions');
      if (error) {
        console.error('Error updating ended subscriptions:', error);
      } else {
        console.log('DEBUG: Successfully updated ended subscriptions');
      }
    } catch (error) {
      console.error('Error calling update_ended_subscriptions function:', error);
    }
  };

  const loadEndedSubscriptions = async () => {
    if (!user?.id) return;

    try {
      console.log('DEBUG: Loading ended subscriptions for user:', user.id);
      
      const { data: applications, error } = await supabase
        .from('applications')
        .select(`
          id,
          application_id,
          status,
          created_at,
          message,
          subscription_end_date,
          mentor_id,
          course_id,
          courses!inner (
            id,
            title,
            price,
            course_type
          )
        `)
        .eq('user_id', user.id)
        .eq('is_subscription_active', false)
        .not('subscription_end_date', 'is', null)
        .eq('courses.course_type', 'subscription')
        .order('subscription_end_date', { ascending: false });

      console.log('DEBUG: Found ended subscription applications:', applications);

      if (error) {
        console.error('Error loading ended subscriptions:', error);
        return;
      }

      if (!applications || applications.length === 0) {
        setEndedSubscriptions([]);
        return;
      }

      // Fetch mentor profiles and check for existing reviews
      const enrichedSubscriptions: EndedSubscription[] = [];
      
      for (const app of applications) {
        // Fetch mentor profile
        const { data: mentorData, error: mentorError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url')
          .eq('id', app.mentor_id)
          .single();

        if (mentorError) {
          console.error('Error fetching mentor profile:', mentorError);
          continue;
        }

        // Check if review exists for this subscription in subscription_reviews table
        const { data: existingReview } = await supabase
          .from('subscription_reviews')
          .select('id')
          .eq('reviewer_id', user.id)
          .eq('reviewee_id', app.mentor_id)
          .eq('application_id', app.application_id) // Using application_id for subscriptions in subscription_reviews
          .eq('review_type', 'mentee_to_mentor')
          .single();

        const hasReview = !!existingReview;
        console.log('DEBUG: Application', app.id, 'has review:', hasReview);
        
        // Only include subscriptions without reviews
        if (!hasReview && app.courses && mentorData) {
          enrichedSubscriptions.push({
            id: app.id,
            application_id: app.application_id,
            status: 'completed',
            created_at: app.created_at,
            message: app.message || '',
            subscription_end_date: app.subscription_end_date,
            course: {
              id: app.courses.id,
              title: app.courses.title,
              price: app.courses.price,
              course_type: app.courses.course_type,
            },
            mentor: {
              id: mentorData.id,
              first_name: mentorData.first_name,
              last_name: mentorData.last_name,
              avatar_url: mentorData.avatar_url,
            },
            hasReview,
            isSubscription: true,
          });
        }
      }

      console.log('DEBUG: Ended subscriptions without reviews:', enrichedSubscriptions);
      setEndedSubscriptions(enrichedSubscriptions);
      
      // Notify parent component about pending reviews status after both bookings and subscriptions are loaded
      setTimeout(() => {
        const totalPendingReviews = bookings.length + enrichedSubscriptions.length;
        onPendingReviewsChange?.(totalPendingReviews > 0);
      }, 100);
    } catch (error) {
      console.error('Error loading ended subscriptions:', error);
      setEndedSubscriptions([]);
      onPendingReviewsChange?.(false);
    }
  };

  const filterBookingsWithoutReviews = async (bookings: SessionBookingWithMentor[]) => {
    console.log('DEBUG: Starting to filter bookings without reviews, total bookings:', bookings.length);
    console.log('DEBUG: Current user ID:', user?.id);
    console.log('DEBUG: Bookings data:', bookings.map(b => ({ 
      id: b.id, 
      status: b.status, 
      mentor_id: b.mentor_id,
      mentee_id: b.mentee_id,
      mentor_name: b.mentor?.first_name 
    })));
    
    if (!user?.id || bookings.length === 0) {
      console.log('DEBUG: No user ID or no bookings to filter');
      return bookings;
    }

    try {
      // Get all reviews for these bookings using id as booking_id
      const bookingIds = bookings.map(booking => booking.id);
      console.log('DEBUG: Booking IDs to check for reviews:', bookingIds);
      
      const { data: reviews, error } = await supabase
        .from('appointment_reviews')
        .select('booking_id, reviewer_id, reviewee_id')
        .in('booking_id', bookingIds)
        .eq('reviewer_id', user.id);

      if (error) {
        console.error('Error fetching reviews:', error);
        return bookings;
      }

      console.log('DEBUG: Found reviews:', reviews);
      console.log('DEBUG: Review query - looking for reviewer_id:', user.id);
      const reviewedBookingIds = new Set(reviews?.map(r => r.booking_id) || []);
      console.log('DEBUG: Reviewed booking IDs set:', reviewedBookingIds);
      
      const filteredBookings = bookings.filter(booking => {
        const hasReview = reviewedBookingIds.has(booking.id);
        console.log(`DEBUG: Booking ${booking.id} - hasReview: ${hasReview}`);
        return !hasReview;
      });
      
      console.log('DEBUG: Filtered bookings (without reviews):', filteredBookings.length);
      console.log('DEBUG: Filtered booking details:', filteredBookings.map(b => ({ 
        id: b.id, 
        mentor: b.mentor?.first_name,
        status: b.status 
      })));
      
      return filteredBookings;
    } catch (error) {
      console.error('Error filtering bookings:', error);
      return bookings;
    }
  };

  const handleLeaveReview = (item: CompletedItem) => {
    setSelectedItem(item);
    setIsReviewDialogOpen(true);
  };

  const handleViewDetails = (item: CompletedItem) => {
    // Only show details for session bookings, not subscriptions
    if (!('isSubscription' in item)) {
      setSelectedItem(item);
      setIsDetailsDialogOpen(true);
    }
  };

  const handleReviewSubmitted = () => {
    console.log('DEBUG: Review submitted, refreshing data');
    // Remove the item from the appropriate list since it now has a review
    if (selectedItem) {
      if ('isSubscription' in selectedItem && selectedItem.isSubscription) {
        console.log('DEBUG: Removing subscription from pending reviews list');
        setEndedSubscriptions(prev => prev.filter(s => s.id !== selectedItem.id));
      } else {
        console.log('DEBUG: Removing session booking from pending reviews list');
        setBookings(prev => prev.filter(b => b.id !== selectedItem.id));
      }
    }
    setSelectedItem(null);
    setIsReviewDialogOpen(false);
    
    // Trigger a custom event to notify other components (like MentorApplications)
    window.dispatchEvent(new CustomEvent('review-submitted', {
      detail: { 
        reviewerId: user?.id,
        itemId: selectedItem?.id,
        isSubscription: 'isSubscription' in selectedItem! && selectedItem!.isSubscription
      }
    }));
  };

  const isSessionBooking = (item: CompletedItem): item is SessionBookingWithMentor => {
    return !('isSubscription' in item);
  };

  const isEndedSubscription = (item: CompletedItem): item is EndedSubscription => {
    return 'isSubscription' in item && item.isSubscription;
  };

  const renderCompletedItem = (item: CompletedItem) => {
    if (isSessionBooking(item)) {
      // Render session booking
      return (
        <Card key={item.id} className="p-3 sm:p-4 border-l-4 border-orange-200 bg-orange-50">
          <CardContent className="p-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3 sm:gap-4">
                <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                  <AvatarImage src={item.mentor.avatar_url} />
                  <AvatarFallback>
                    {item.mentor.first_name[0]}{item.mentor.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                    {item.mentor.first_name} {item.mentor.last_name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                      {format(convertFromUTC(item.booked_date, item.start_time, userTimezone).localDate, 'EEE, MMM d')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                      {convertFromUTC(item.booked_date, item.start_time, userTimezone).localTime} - {convertFromUTC(item.booked_date, item.end_time, userTimezone).localTime}
                    </span>
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">
                      Awaiting Review
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewDetails(item)}
                  className="text-xs sm:text-sm"
                >
                  View Details
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleLeaveReview(item)}
                  className="bg-emu-blue hover:bg-emu-blue/90 text-xs sm:text-sm"
                >
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Leave Review
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    } else {
      // Render ended subscription
      return (
        <Card key={item.id} className="p-3 sm:p-4 border-l-4 border-purple-200 bg-purple-50">
          <CardContent className="p-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3 sm:gap-4">
                <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                  <AvatarImage src={item.mentor.avatar_url} />
                  <AvatarFallback>
                    {item.mentor.first_name[0]}{item.mentor.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                    {item.mentor.first_name} {item.mentor.last_name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">{item.course.title}</p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Repeat className="h-3 w-3 sm:h-4 sm:w-4" />
                      Subscription
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                      Ended {format(
                        new Date(
                          parseInt(item.subscription_end_date.substring(0, 4)),
                          parseInt(item.subscription_end_date.substring(5, 7)) - 1,
                          parseInt(item.subscription_end_date.substring(8, 10))
                        ),
                        'MMM d, yyyy'
                      )}
                    </span>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                      Awaiting Review
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex w-full sm:w-auto">
                <Button
                  size="sm"
                  onClick={() => handleLeaveReview(item)}
                  className="bg-emu-blue hover:bg-emu-blue/90 text-xs sm:text-sm w-full sm:w-auto"
                >
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Leave Review
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const allCompletedItems = [...bookings, ...endedSubscriptions];
  const totalItems = allCompletedItems.length;

  // Sort all items by completion date (most recent first)
  const sortedItems = allCompletedItems.sort((a, b) => {
    const dateA = isSessionBooking(a) ? new Date(a.booked_date) : new Date(a.subscription_end_date);
    const dateB = isSessionBooking(b) ? new Date(b.booked_date) : new Date(b.subscription_end_date);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <>
      <div className="space-y-4">
        {sortedItems.slice(0, 5).map(renderCompletedItem)}
        
        {totalItems > 5 && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" className="text-emu-blue">
              View All Items Awaiting Review ({totalItems - 5} more)
            </Button>
          </div>
        )}
      </div>

      {selectedItem && isReviewDialogOpen && (
        <ReviewDialog
          isOpen={isReviewDialogOpen}
          onClose={() => {
            setIsReviewDialogOpen(false);
            setSelectedItem(null);
          }}
          bookingId={isEndedSubscription(selectedItem) ? selectedItem.application_id : selectedItem.id}
          revieweeId={isSessionBooking(selectedItem) ? selectedItem.mentor_id : selectedItem.mentor.id}
          revieweeName={isSessionBooking(selectedItem) 
            ? `${selectedItem.mentor.first_name} ${selectedItem.mentor.last_name}`
            : `${selectedItem.mentor.first_name} ${selectedItem.mentor.last_name}`
          }
          onReviewSubmitted={handleReviewSubmitted}
          isSubscriptionReview={isEndedSubscription(selectedItem)}
        />
      )}

      {selectedItem && isDetailsDialogOpen && isSessionBooking(selectedItem) && (
        <MenteeAppointmentDetailsDialog
          booking={selectedItem}
          isOpen={isDetailsDialogOpen}
          onClose={() => {
            setIsDetailsDialogOpen(false);
            setSelectedItem(null);
          }}
        />
      )}
    </>
  );
};

export default CompletedAppointments;
